<script setup lang="ts">
import Icon from '@/components/icon/Index.vue'

import { computed, ref, toRefs } from 'vue'
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/vue/24/solid'

import type { Sort } from '@ui/types/table'

const props = withDefaults(
  defineProps<{
    columns?: any[]
    data?: any[]
    sortable?: boolean | undefined
    striped?: boolean
    stickyHeader?: boolean
    hover?: boolean
  }>(),
  {
    columns: Array,
    data: Array,
    sortable: true,
    striped: false,
    stickyHeader: false,
    hover: false
  }
)

const { columns, data, sortable, striped, stickyHeader } = toRefs(props)

const sorting = ref<{ [key: string]: Sort }>({})

const computedColumns = computed(() =>
  columns.value.map((column) => ({
    ...column,
    sortable: column.sortable !== false
  }))
)

const sortedData = computed(() => {
  if (sortable.value !== true || !columns.value || !data.value) return data.value

  const sortedColumns = Object.keys(sorting.value).filter((key) => sorting.value[key])

  if (sortedColumns.length === 0) return data.value

  const sortedData = [...data.value]

  sortedData.sort((a, b) => {
    for (const key of sortedColumns) {
      const column = columns.value.find((col) => col.field === key)
      if (!column) continue

      const valueA = a[key] || ''
      const valueB = b[key] || ''

      // Use localeCompare for string comparison
      const comparison = valueA.localeCompare(valueB)

      if (comparison !== 0) {
        return sorting.value[key] === 'asc' ? comparison : -comparison
      }
    }
    return 0
  })

  return sortedData
})

const handleSort = (column: any) => {
  const isColumnSortable = !sortable.value && !column.sortable
  if (isColumnSortable) return

  // Toggle sorting for the selected column
  if (sorting.value[column.field] === 'asc') {
    sorting.value[column.field] = 'desc'
  } else if (sorting.value[column.field] === 'desc') {
    sorting.value[column.field] = undefined // Reset to undefined
  } else {
    sorting.value[column.field] = 'asc'
  }

  // Reset sorting for all other columns except the one being sorted
  Object.keys(sorting.value).forEach((key) => {
    if (key !== column.field && sorting.value[key] !== undefined) {
      sorting.value[key] = undefined // Reset to undefined
    }
  })
}
</script>

<template>
  <table
    class="table w-full rounded-md relative"
    :class="{ striped: striped, hover: hover }"
  >
    <thead>
      <tr>
        <th
          v-for="column in computedColumns"
          :key="column.field"
          :class="[
            'bg-[#efecf7]',
            {
              'cursor-pointer select-none': sortable !== false && column.sortable,
              'sticky top-0': stickyHeader
            }
          ]"
          @click="handleSort(column)"
        >
          <div class="flex items-center space-x-1">
            <div>{{ column.label }}</div>
            <div
              :class="{
                'opacity-100': sortable && column.sortable,
                'opacity-0': !sortable || !column.sortable
              }"
            >
              <Icon
                :icon="
                  sorting[column.field] === 'asc'
                    ? ChevronUpIcon
                    : sorting[column.field] === 'desc'
                      ? ChevronDownIcon
                      : ChevronUpDownIcon
                "
                size="sm"
              />
            </div>
          </div>
        </th>
      </tr>
    </thead>
    <tbody class="divide-y">
      <tr
        v-for="(row, rowIndex) in sortedData"
        :key="rowIndex"
      >
        <td
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
        >
          <slot
            :name="`column-${column.field}`"
            :value="row[column.field]"
            :row="row"
          >
            {{ row[column.field] }}
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped lang="scss">
.table {
  &.striped {
    &:deep(tr:nth-child(even)) {
      @apply bg-primary/5;
    }
  }

  &.hover {
    &:deep(tr:nth-child(odd):hover) {
      @apply bg-primary/5;
    }
  }

  :deep(th) {
    @apply text-left font-medium;
  }

  :deep(th),
  :deep(td) {
    @apply whitespace-nowrap p-2;
  }
}
</style>
